import { canEditSection } from '@/lib/access';
import React, { useEffect, useState } from 'react';
import { useManualForm, FormShell, NumberField, TextAreaField, RepeatTable, ReadOnlyField } from './_shared';
import { Card } from '@/components/UI';
import api from '@/lib/api';
import { useCycle } from '@/lib/cycle';
import { formatCurrency } from '@/lib/format';

const DEFAULT = {
  summary: {
    total_complaints: 0,
    copq_total: 0,
    internal_rejection_qty: 0,
    internal_scrap_qty: 0,
    internal_rework_qty: 0,
    internal_return_plating_qty: 0,
    internal_return_supplier_qty: 0,
    scrap_ppm: 0,
    rework_ppm: 0,
    copq_press_shop: 0,
    copq_machine_shop: 0,
    copq_final_inspection: 0,
    notes: '',
  },
  complaints: [],
  customer_ppm: [],
  audits: [],
  customer_scorecards: [],
};

const COMP_COLS = [
  { key: 'sr_no', label: '#', type: 'number' },
  { key: 'customer', label: 'Customer' },
  { key: 'part_name', label: 'Part Name' },
  { key: 'part_number', label: 'Part Number' },
  { key: 'issue_description', label: 'Issue', type: 'textarea' },
  { key: 'complaint_date', label: 'Complaint Date', type: 'date' },
  { key: 'eight_d_status', label: '8D Status', type: 'select', options: [{ value: 'open', label: 'Open' }, { value: 'in_progress', label: 'In Progress' }, { value: 'closed', label: 'Closed' }] },
  { key: 'root_cause', label: 'Root Cause' },
  { key: 'corrective_action', label: 'Corrective Action' },
  { key: 'target_closure_date', label: 'Target Closure', type: 'date' },
  { key: 'qty_affected', label: 'Qty Affected', type: 'number', right: true },
  { key: 'cost_impact', label: 'Cost Impact (₹)', type: 'number', right: true },
];

const PPM_COLS = [
  { key: 'customer', label: 'Customer' },
  { key: 'ppm', label: 'PPM', type: 'number', right: true },
  { key: 'target_ppm', label: 'Target PPM', type: 'number', right: true },
  { key: 'status', label: 'Status', type: 'select', options: ['green', 'amber', 'red'] },
];

const AUDIT_COLS = [
  { key: 'audit_type', label: 'Audit Type', type: 'select', options: ['VDA 6.3', 'IATF 16949', 'Customer', 'Internal'] },
  { key: 'auditor', label: 'Auditor' },
  { key: 'date', label: 'Date', type: 'date' },
  { key: 'score', label: 'Score', type: 'number', right: true },
  { key: 'max_score', label: 'Max', type: 'number', right: true },
  { key: 'major_findings', label: 'Major', type: 'number', right: true },
  { key: 'minor_findings', label: 'Minor', type: 'number', right: true },
  { key: 'observations', label: 'Obs', type: 'number', right: true },
  { key: 'status', label: 'Status', type: 'select', options: ['open', 'closed'] },
  { key: 'remarks', label: 'Remarks' },
];

const SC_COLS = [
  { key: 'customer', label: 'Customer' },
  { key: 'quality_rating', label: 'Quality', type: 'select', options: ['A', 'B', 'C', 'D'] },
  { key: 'delivery_rating', label: 'Delivery', type: 'select', options: ['A', 'B', 'C', 'D'] },
  { key: 'overall_rating', label: 'Overall', type: 'select', options: ['A', 'B', 'C', 'D'] },
  { key: 'trend', label: 'Trend', type: 'select', options: ['improving', 'stable', 'declining'] },
  { key: 'remarks', label: 'Remarks' },
];

export default function Quality() {
  const ctx = useManualForm('quality', DEFAULT);
  const { currentUnit } = useCycle();
  const canEdit = canEditSection(ctx.user, 'quality');
  const locked = ctx.currentCycle?.status === 'locked';
  const s = ctx.data.summary || DEFAULT.summary;
  const set = (key, val) => ctx.setData({ ...ctx.data, summary: { ...s, [key]: val } });
  const setKey = (k, rows) => ctx.setData({ ...ctx.data, [k]: rows });

  const [prodSrc, setProdSrc] = useState(null); // {parsed_at} | 'none' | null
  const [customerOpts, setCustomerOpts] = useState([]);

  useEffect(() => {
    api.get('/customers?active_only=true')
      .then((r) => setCustomerOpts((r.data || []).map((c) => c.name)))
      .catch(() => {});
  }, []);

  const withCustomer = (cols) =>
    cols.map((c) => (c.key === 'customer' ? { ...c, type: 'select', options: customerOpts } : c));
  const scCols = [...withCustomer(SC_COLS), { key: 'attachment', label: 'Scorecard File', type: 'file' }];

  // Auto-fill the internal-quality block from the production JSON for this unit+cycle.
  useEffect(() => {
    if (ctx.loading || !ctx.currentCycle || !currentUnit) return;
    let active = true;
    api.get(`/data/production/${ctx.currentCycle.year}/${ctx.currentCycle.month}?unit=${currentUnit.code}`)
      .then((r) => {
        if (!active) return;
        if (!r.data?.has_data) { setProdSrc('none'); return; }
        const d = r.data.data || {};
        const q = d.quality || {};
        const bd = q.by_department || {};
        const produced = Number(d.total_actual) || 0;
        const scrap = Number(q.total_scrap_qty) || 0;
        const rework = Number(q.total_rework_qty) || 0;
        const scrap_ppm = produced ? Math.round((scrap / produced) * 1e6) : 0;
        const rework_ppm = produced ? Math.round((rework / produced) * 1e6) : 0;
        const cur = ctx.data.summary || {};
        const next = {
          ...cur,
          copq_total: Math.round(Number(q.total_copq) || 0),
          internal_rejection_qty: Number(q.total_rejection_qty) || 0,
          internal_scrap_qty: scrap,
          internal_rework_qty: rework,
          internal_return_plating_qty: Number(q.total_return_plating_qty) || 0,
          internal_return_supplier_qty: Number(q.total_return_supplier_qty) || 0,
          scrap_ppm,
          rework_ppm,
          copq_press_shop: Math.round(Number(bd.press_shop?.copq) || 0),
          copq_machine_shop: Math.round(Number(bd.machine_shop?.copq) || 0),
          copq_final_inspection: Math.round(Number(bd.final_inspection?.copq) || 0),
        };
        const keys = ['copq_total','internal_rejection_qty','internal_scrap_qty','internal_rework_qty','internal_return_plating_qty','internal_return_supplier_qty','scrap_ppm','rework_ppm','copq_press_shop','copq_machine_shop','copq_final_inspection'];
        const changed = keys.some((k) => (Number(cur[k]) || 0) !== (Number(next[k]) || 0));
        if (changed) ctx.setData({ ...ctx.data, summary: next });
        setProdSrc({ parsed_at: r.data.parsed_at });
      })
      .catch(() => { if (active) setProdSrc('none'); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.loading, ctx.currentCycle?.id, currentUnit?.code]);

  const inr = (n) => (Number(n) || 0).toLocaleString('en-IN');

  return (
    <FormShell title="Quality & Customer Performance" subtitle="Complaints, COPQ, PPM, audits and scorecards" status={ctx.status} save={ctx.save} submit={ctx.submit} saving={ctx.saving} submitting={ctx.submitting} canEdit={canEdit} locked={locked}>
      <Card
        title="Internal Quality — auto-filled from Production JSON"
        action={<span className="text-xs text-slate-500">{prodSrc && prodSrc !== 'none' ? '✓ Synced from Production JSON • read-only' : prodSrc === 'none' ? 'No production data for this unit / cycle' : ''}</span>}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <ReadOnlyField label="PPM (Scrap)" value={inr(s.scrap_ppm)} hint="Scrap ÷ Produced × 1,000,000 — excludes rework" />
          <ReadOnlyField label="Rework (pcs)" value={inr(s.internal_rework_qty)} hint="Actual 'Rework' disposition" />
          <ReadOnlyField label="Rework PPM" value={inr(s.rework_ppm)} />
          <ReadOnlyField label="COPQ Total (₹)" value={formatCurrency(s.copq_total)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <ReadOnlyField label="Rejection (total qty)" value={inr(s.internal_rejection_qty)} />
          <ReadOnlyField label="Scrap (pcs)" value={inr(s.internal_scrap_qty)} />
          <ReadOnlyField label="Return to Plating (pcs)" value={inr(s.internal_return_plating_qty)} />
          <ReadOnlyField label="Return to Supplier (pcs)" value={inr(s.internal_return_supplier_qty)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <ReadOnlyField label="COPQ — Press Shop (₹)" value={formatCurrency(s.copq_press_shop)} />
          <ReadOnlyField label="COPQ — Machine Shop (₹)" value={formatCurrency(s.copq_machine_shop)} />
          <ReadOnlyField label="COPQ — Final Inspection (₹)" value={formatCurrency(s.copq_final_inspection)} />
        </div>
      </Card>

      <Card title="Customer Quality (manual)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NumberField label="Total Complaints" value={s.total_complaints} onChange={(v) => set('total_complaints', v)} disabled={!canEdit || locked} />
        </div>
        <div className="mt-3"><TextAreaField label="Notes" value={s.notes} onChange={(v) => set('notes', v)} disabled={!canEdit || locked} /></div>
      </Card>

      <Card title="Customer Complaints (8D)">
        <RepeatTable rows={ctx.data.complaints || []} columns={withCustomer(COMP_COLS)} onChange={(rows) => setKey('complaints', rows)} addLabel="Add Complaint" defaultRow={{ sr_no: (ctx.data.complaints || []).length + 1, eight_d_status: 'open' }} />
      </Card>
      <Card title="Customer PPM Watch">
        <RepeatTable rows={ctx.data.customer_ppm || []} columns={withCustomer(PPM_COLS)} onChange={(rows) => setKey('customer_ppm', rows)} addLabel="Add Customer" defaultRow={{ ppm: 0, target_ppm: 50, status: 'amber' }} />
      </Card>
      <Card title="Audit Summary">
        <RepeatTable rows={ctx.data.audits || []} columns={AUDIT_COLS} onChange={(rows) => setKey('audits', rows)} addLabel="Add Audit" defaultRow={{ audit_type: 'Internal', score: 0, max_score: 100, status: 'open' }} />
      </Card>
      <Card title="Customer Scorecards">
        <RepeatTable rows={ctx.data.customer_scorecards || []} columns={scCols} onChange={(rows) => setKey('customer_scorecards', rows)} addLabel="Add Customer" defaultRow={{ quality_rating: 'B', delivery_rating: 'B', overall_rating: 'B', trend: 'stable' }} />
      </Card>
    </FormShell>
  );
}
